import React, { useState } from "react";
import { API } from "../utils/api";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, LogIn, AlertCircle, Loader2 } from "lucide-react";
import logo from '../asset/kestren.png';

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await API.post("/admin/login", form);
      localStorage.setItem("token", res.data.token);
      window.dispatchEvent(new Event("tokenChanged"));
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 flex items-center justify-center p-4">
      {/* Subtle Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden">

          {/* Header with Logo */}
          <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-10 text-center">
            <img
              src={logo}
              alt="Wallet App Logo"
              className="w-32 h-32 mx-auto mb-5 rounded-3xl shadow-2xl border-8 border-white/20"
            />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Admin Portal
            </h1>
          </div>

          {/* Form Section */}
          <div className="p-8 pt-10 bg-white">
            <h2 className="text-2xl font-bold text-gray-800 text-center mb-8">
              Welcome Back
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@example.com"
                  className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent transition-all placeholder-gray-500 font-medium text-gray-800"
                  required
                />
              </div>

              {/* Password */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent transition-all placeholder-gray-500 font-medium text-gray-800"
                  required
                />
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-3 transition-all duration-300 shadow-lg ${loading
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black transform hover:scale-105 shadow-gray-600/40"
                  }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="w-6 h-6" />
                    Login to Dashboard
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <p className="text-center text-xs text-gray-500 mt-10 font-medium tracking-wider">
              © {new Date().getFullYear()} KESTREN • Powered by KESTREN
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}